import {Card, showNotification} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';

import {
  useGetParticipationQuery,
  usePatchAdvertisedVehicleInAdvertisementUpdatedAtMutation,
} from '@dms/api/core';
import {useGetVehicleInsightsQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {
  useGetVehicleWarehouseManagementSettingsQuery,
  usePatchSaleVehicleStockInDateMutation,
} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {CommentsWidget, handleApiError, usePermissions, VehicleNoteWidget} from '@dms/shared';

import {getApiDateString, localDateToUtcDate, parseDate} from 'shared/web';

import {InsightItem} from './InsightItem';
import {VehicleSaleDatesInsightItem} from './VehicleSaleDatesInsightItem';

interface VehicleInsightsCardProps {
  vehicleId: string;
}

// When another item is added, we might want to ask designers to come up with a new design
export function VehicleInsightsCard(props: VehicleInsightsCardProps) {
  const {data: vehicleInsights, refetch: refetchVehicleInsights} = useGetVehicleInsightsQuery({
    vehicleId: props.vehicleId,
  });
  const {data: saleVehicle, refetch: refetchSaleVehicleQuery} = useGetSaleVehicleQuery({
    vehicleId: props.vehicleId,
  });
  const {data: warehouseSettings, isLoading: isWarehouseSettingsQueryLoading} =
    useGetVehicleWarehouseManagementSettingsQuery();

  const [patchUpdatedAtMutation, {isLoading: isPatchUpdatedAtMutationLoading}] =
    usePatchAdvertisedVehicleInAdvertisementUpdatedAtMutation();
  const [patchStockInAtMutation, {isLoading: isPatchStockInAtMutationLoading}] =
    usePatchSaleVehicleStockInDateMutation();

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const [
    hasVehicleCommentReadPermission,
    hasVehicleCommentAddPermission,
    hasVehicleCommentDeletePermission,
    canEditPublishedDate,
    canEditStockInDate,
    canReadVehicleWarehouseInformation,
    canReadAdvertisingSettings,
    canReadVehicleSellingPrice,
    canReadVehiclePurchasePrice,
    canReadBusinessCase,
  ] = usePermissions({
    permissionKeys: [
      'vehicleCommentRead',
      'vehicleCommentAdd',
      'vehicleCommentDelete',
      'vehicleEditPublishedDate',
      'vehicleStockInDate',
      'vehicleWarehouseReadInformation',
      'readAdvertisingSettings',
      'vehicleSellingPrice',
      'vehiclePurchasePrice',
      'businessCaseRead',
    ],
    scopes: {
      vehicleCommentAdd: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleCommentRead: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleEditPublishedDate: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleStockInDate: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleSellingPrice: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehiclePurchasePrice: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleCommentDelete: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
    },
  });

  const isSalesModuleEnabled = canReadBusinessCase;

  const canShowVehicleOverview =
    (canReadVehicleSellingPrice && canReadVehiclePurchasePrice) ||
    canReadAdvertisingSettings ||
    canReadVehicleWarehouseInformation;

  return (
    <VStack spacing={4}>
      <Show when={canShowVehicleOverview && isSalesModuleEnabled}>
        <Card title={i18n.t('entity.interest.labels.overviewTab')}>
          <Box padding={4}>
            <HStack spacing={4} wrap>
              <Show when={vehicleInsights}>
                <Show when={canReadVehicleWarehouseInformation}>
                  <InsightItem
                    value={vehicleInsights?.daysInStock}
                    title={i18n.t('entity.vehicle.labels.overview.daysOnStock')}
                    subtitle={i18n.t('entity.vehicle.labels.overview.stockInDate')}
                    editDialogModalLabel={i18n.t(
                      'entity.vehicle.labels.overview.editModal.stockInDate'
                    )}
                    {...(saleVehicle?.stockInDate && {date: parseDate(saleVehicle?.stockInDate)})}
                    isMutationLoading={isPatchStockInAtMutationLoading}
                    onEdit={
                      !isWarehouseSettingsQueryLoading &&
                      !warehouseSettings?.isEnabled &&
                      canEditStockInDate
                        ? (date) =>
                            patchStockInAtMutation({
                              vehicleId: props.vehicleId,
                              patchSaleVehicleStockInDateRequestBody: {
                                stockInDate: getApiDateString(localDateToUtcDate(date)),
                              },
                            })
                              .unwrap()
                              .then(async () => {
                                showNotification.success(
                                  i18n.t('general.notifications.changesSuccessfullySaved')
                                );
                                await refetchSaleVehicleQuery();
                                await refetchVehicleInsights();
                                return true;
                              })
                              .catch((error: Error) => {
                                handleApiError(error);
                                return false;
                              })
                        : undefined
                    }
                  />
                </Show>
                <Show when={canReadAdvertisingSettings}>
                  <InsightItem
                    value={vehicleInsights?.daysInAdvertising}
                    title={i18n.t('entity.vehicle.labels.overview.daysAdvertized')}
                    subtitle={i18n.t('entity.vehicle.labels.overview.publishedOn')}
                    editDialogModalLabel={i18n.t(
                      'entity.vehicle.labels.overview.editModal.publishedOn'
                    )}
                    isMutationLoading={isPatchUpdatedAtMutationLoading}
                    onEdit={
                      canEditPublishedDate
                        ? (date) =>
                            patchUpdatedAtMutation({
                              vehicleId: props.vehicleId,
                              patchAdvertisedVehicleInAdvertisementUpdatedAtRequestBody: {
                                inAdvertisementUpdatedAt: localDateToUtcDate(date).toISOString(),
                              },
                            })
                              .unwrap()
                              .then(async () => {
                                showNotification.success(
                                  i18n.t('general.notifications.changesSuccessfullySaved')
                                );
                                await refetchVehicleInsights();
                                return true;
                              })
                              .catch((error: Error) => {
                                handleApiError(error);
                                return false;
                              })
                        : undefined
                    }
                  />
                </Show>
                <Show when={canReadVehiclePurchasePrice && canReadVehicleSellingPrice}>
                  <InsightItem
                    value={vehicleInsights?.daysSinceReprice}
                    title={i18n.t('entity.vehicle.labels.overview.daysFromReprice')}
                    subtitle={i18n.t('entity.vehicle.labels.overview.repricedOn')}
                  />
                </Show>
                <VehicleSaleDatesInsightItem
                  vehicleId={props.vehicleId}
                  sellingDate={saleVehicle?.soldAt ? parseDate(saleVehicle.soldAt) : undefined}
                  buyingDate={
                    saleVehicle?.purchaseDate ? parseDate(saleVehicle.purchaseDate) : undefined
                  }
                  data-testid="vehicleInsights-saleDates"
                />
              </Show>
            </HStack>
          </Box>
        </Card>
      </Show>
      <Grid columns={[1, 2]} align="flex-start">
        <Show when={hasVehicleCommentReadPermission}>
          <CommentsWidget
            title={i18n.t('entity.vehicle.labels.vehicleComments')}
            resourceId={EntityResourceIds.vehicle}
            recordId={props.vehicleId}
            isAddDisabled={!hasVehicleCommentAddPermission}
            isDeleteDisabled={!hasVehicleCommentDeletePermission}
          />
        </Show>
        <VehicleNoteWidget
          vehicleId={props.vehicleId}
          title={i18n.t('entity.vehicle.labels.vehicleAdditionalNotes')}
        />
      </Grid>
    </VStack>
  );
}
